import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Pause, Play } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { api, apiError } from "../../lib/api";
import ExportCsvButton from "../../components/ExportCsvButton";

export default function AdminUsers() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [role, setRole] = useState("all");
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

const refresh = useCallback(() => {
    setLoading(true);
    const params = {};
    if (role !== "all") params.role = role;
    if (active !== "all") params.is_active = active === "active";
    if (search.trim()) params.search = search.trim();
    api.get("/admin/users", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [role, active, search]);
 
  useEffect(() => {
  const t = setTimeout(refresh, 200);
  return () => clearTimeout(t);
}, [refresh]);

  const act = async (id, kind) => {
    try {
      await api.post(`/admin/users/${id}/${kind}`);
      toast.success(kind === "suspend" ? "User suspended" : "User activated");
      refresh();
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ users</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Users</h1>
        <p className="text-sm text-mb-fg2 mt-1">Search across all roles and tenants. Suspending immediately revokes access.</p>
      </div>

      <div className="flex justify-end">
        <ExportCsvButton
          endpoint="/api/admin/exports/users.csv"
          filename="users"
          params={{
            role: role !== "all" ? role : undefined,
            is_active: active !== "all" ? (active === "active") : undefined,
            search: search.trim() || undefined,
          }}
          testid="admin-users-export"
        />
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid sm:grid-cols-[180px_180px_1fr] gap-3">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="users-role" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-mb-surface2 border-mb-border text-white">
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="super_admin">Super admin</SelectItem>
            <SelectItem value="hospital_admin">Hospital admin</SelectItem>
            <SelectItem value="doctor">Doctor</SelectItem>
            <SelectItem value="patient">Patient</SelectItem>
          </SelectContent>
        </Select>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger data-testid="users-active" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-mb-surface2 border-mb-border text-white">
            <SelectItem value="all">Any state</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mb-fg2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name"
            data-testid="users-search" className="pl-9 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
        </div>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden">
        <div className="px-5 py-3 border-b border-mb-border text-xs uppercase tracking-[0.2em] text-mb-fg2" data-testid="users-count">{data.total} match{data.total !== 1 ? "es" : ""}</div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center" data-testid="users-empty">No users.</div>
        ) : (
          <ul className="divide-y divide-mb-border">
            {data.items.map((u) => (
              <li key={u.id} className="px-5 py-3 grid grid-cols-1 sm:grid-cols-[1fr_140px_140px_auto] gap-2 sm:items-center"
                  data-testid={`user-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{u.full_name}</div>
                  <div className="text-xs text-mb-fg2 truncate">{u.email}</div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2 sm:justify-self-start">{u.role}</span>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${u.is_active ? "bg-teal-500/10 text-teal-300 border-teal-500/20" : "bg-zinc-800 text-mb-fg2"} sm:justify-self-start`}>
                  {u.is_active ? "active" : "inactive"}
                </span>
                <div className="flex gap-2 justify-start sm:justify-end">
                  {u.role !== "super_admin" && (u.is_active ? (
                    <Button onClick={() => act(u.id, "suspend")} variant="ghost" data-testid={`user-suspend-${u.id}`}
                      className="hover:bg-red-500/10 text-red-400 h-8"><Pause className="h-4 w-4" /></Button>
                  ) : (
                    <Button onClick={() => act(u.id, "activate")} variant="ghost" data-testid={`user-activate-${u.id}`}
                      className="hover:bg-teal-500/10 text-teal-300 h-8"><Play className="h-4 w-4" /></Button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
